import {
    Box,
    Button,
    createStyles,
    CssBaseline,
    Grid,
    Link as MuiLink,
    makeStyles,
    MuiThemeProvider,
    Theme,
    Typography
} from '@material-ui/core';
import {createTheme} from '@material-ui/core/styles';
import ExpandDownIcon from '@material-ui/icons/ExpandMore';
import Alert from '@material-ui/lab/Alert';
import React, {useEffect, useLayoutEffect} from 'react';
import ReactGA from 'react-ga';
import {BrowserRouter as Router, Route, Switch, useLocation} from "react-router-dom";
import {careers} from './Careers';
import ClearFlaskLogo from './ClearFlaskLogo';
import {ColumnHeader, ColumnItem, ColumnRow} from "./ColumnItem";

const theme = createTheme({
    palette: {primary: {main: '#939363'}}
});

const useStyles = makeStyles((theme: Theme) => createStyles({
    smotanaBrandName: {
        fontFamily: "'Raleway', sans-serif",
        fontWeight: 100,
        color: "#939363ff",
    },
    app: {
        fontFamily: '"Hind", sans-serif',
        textAlign: "center",
        margin: "auto",
        padding: 10,
        maxWidth: 1024,
    },
    imgFit: {
        maxWidth: "100%",
        maxHeight: "100%",
        display: "inline-block",
    },
    imgProjectContainer: {},
    imgProject: {
        margin: 15,
        borderRadius: 15,
        width: 'auto',
        height: 'auto',
        maxWidth: '100%',
        maxHeight: '100%',
    },
    contentProject: {
        maxWidth: 200,
        [theme.breakpoints.down('xs')]: {
            textAlign: 'left',
            margin: 'auto',
        }
    },
    centerVerticalContainer: {
        minHeight: 768,
        position: 'relative',
    },
    projectHeading: {
        margin: theme.spacing(2),
    },
    expandDownIcon: {
        position: 'absolute',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        opacity: 0.1,
        fontSize: '10em',
    },
    centerVerticalInner: {
        position: 'relative',
        top: '50%',
        webkitTransform: 'translateY(-50%)',
        msTransform: 'translateY(-50%)',
        transform: 'translateY(-50%)',
    },
    link: {
        color: 'inherit',
        textDecoration: 'none!important',
        borderBottom: '1px dashed #d0d0d0',
        '&:hover': {
            borderBottomStyle: 'solid',
            borderColor: 'black',
        },
    },
    getInTouch: {
        [theme.breakpoints.down('xs')]: {
            margin: 'auto',
            textAlign: 'center',
        },
    },
}));
export default () => {
    const classes = useStyles();

    useLayoutEffect(() => {
        ReactGA.initialize('UA-127162051-1', {
            gaOptions: {}
        });
        ReactGA.set({
            anonymizeIp: true,
            forceSSL: true
        });
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);

    return (
        <MuiThemeProvider theme={theme}>
            <Router>
                <ScrollToTop/>
                <div className={classes.app}>
                    <CssBaseline/>
                    <Route path='/' render={({location}) => {
                        ReactGA.set({page: location.pathname + location.search});
                        ReactGA.pageview(location.pathname + location.search);
                        return null;
                    }}/>
                    <Switch>
                        <Route exact path="/">
                            <LandingHeader/>
                        </Route>
                        <Route path="*">
                            <Header/>
                        </Route>
                    </Switch>
                    <Switch>
                        {careers.map(career => (
                            <Route exact path={`/careers/${career.slug}`}>{career.render()}</Route>
                        ))}
                        <Route exact path="/">
                            <LandingPage/>
                        </Route>
                        <Route path="*">
                            <Box display='flex' justifyContent='center' alignItems='center' minHeight='30vh'>
                                <Alert variant="outlined" severity="error">Page not found</Alert>
                            </Box>
                        </Route>
                    </Switch>
                    <Footer/>
                </div>
            </Router>
        </MuiThemeProvider>
    );
}

const Footer = () => (
    <React.Fragment>
        <Grid item xs={12}><Box marginTop={20}>&nbsp;</Box></Grid>
        <Grid item xs={12}>
            <small>© Smotana. All rights reserved.</small>
        </Grid>
    </React.Fragment>
);
const LandingPage = () => {
    const classes = useStyles();
    const [showMore, setShowMore] = React.useState(false);
    return (
        <Grid container spacing={3}>
            <Grid item xs={12}><Box marginTop={30}>&nbsp;</Box></Grid>

            <ColumnHeader column={2} title="Ventures"/>

            <ColumnRow>
                <ColumnItem
                    column={1}
                    header={<ClearFlaskLogo/>}
                    description='Open-source Ideation Tool for Feedback, Roadmap and Announcements'
                    codeUrl='https://github.com/clearflask/clearflask'
                    siteUrl='https://clearflask.com'
                />
                <ColumnItem
                    column={2}
                    logo='/img/dataspray-logo.svg'
                    logoAlt='DataSpray'
                    description='Open-source Stream Processing framework for fast development, historical analysis, and affordable per-event cloud pricing'
                    codeUrl='https://github.com/datasprayio/dataspray'
                    siteUrl='https://dataspray.io'
                />
            </ColumnRow>
            <ColumnRow>
                <ColumnItem
                    column={2}
                    logo='/img/localrouter-logo.svg'
                    logoAlt='LocalRouter'
                    title='LocalRouter'
                    description='Local firewall for LLMs, MCPs, and Skills with centralized credential management, model failover, and runtime approval controls.'
                    codeUrl='https://github.com/LocalRouter/LocalRouter'
                    siteUrl='https://localrouter.ai'
                />
                <ColumnItem
                    column={3}
                    logo='/img/netget-logo.svg'
                    logoAlt='NetGet'
                    title='NetGet'
                    description='LLM-controlled network protocol server and client. A Rust CLI where an LLM drives 50+ network protocols via natural language.'
                    codeUrl='https://github.com/matusfaro/netget'
                    siteUrl='https://matusfaro.github.io/netget'
                />
            </ColumnRow>
            <ColumnRow>
                <ColumnItem
                    column={3}
                    logo='/img/zisk-logo.png'
                    logoAlt='Zisk'
                    title='ZISK'
                    description='Innovative Canadian Real Estate platform matching agents to home buyers and providing tech-centered pay-as-you-go services to home buyers'
                    siteUrl='https://zisk.ca'
                />
                <ColumnItem
                    column={4}
                    logo='/img/logo.svg'
                    logoAlt='Smotana'
                    title='Consulting'
                    description='At Smotana, we provide custom services for your software-related needs'
                    email='consulting@smotana.com'
                />
            </ColumnRow>

            <Grid item xs={12}><Box marginTop={10}>&nbsp;</Box></Grid>

            <ColumnHeader column={3} title="Labs"/>

            <ColumnRow>
                <ColumnItem
                    column={3}
                    logo='/img/quarantab-logo.png'
                    logoAlt='QuaranTab'
                    description='Privacy tool for using sensitive data with online tools. Browser extension to cut-off network access to a website to prevent leaking information.'
                    codeUrl='https://github.com/matusfaro/quarantab'
                    siteUrl='https://addons.mozilla.org/en-US/firefox/addon/quarantab/'
                />
                <ColumnItem
                    column={4}
                    logo='/img/ollisten-logo-circle-black.png'
                    logoAlt='Ollisten'
                    title='Ollisten'
                    description='AI Meeting Helper with local LLMs via Ollama. Includes a built-in Agent Editor for customized use cases.'
                    codeUrl='https://github.com/ollisten/ollisten'
                    siteUrl='https://ollisten.com'
                />
            </ColumnRow>

            {!showMore && (
                <Grid item xs={12}>
                    <Box display='flex' justifyContent='flex-end' marginTop={2}>
                        <Button
                            color='primary'
                            endIcon={<ExpandDownIcon/>}
                            onClick={() => setShowMore(true)}
                        >
                            SEE MORE
                        </Button>
                    </Box>
                </Grid>
            )}

            {showMore && (
                <React.Fragment>
                    <ColumnRow>
                        <ColumnItem
                            column={2}
                            logo='/img/zivot-logo.svg'
                            logoAlt='Zivot'
                            title='Zivot'
                            description='Evidence-based mortality risk calculator using scientific research to model personalized health risk factors.'
                            codeUrl='https://github.com/matusfaro/zivot'
                            siteUrl='https://matusfaro.github.io/zivot'
                        />
                        <ColumnItem
                            column={3}
                            logo='/img/matusnomin-logo.png'
                            logoAlt='Wedding Invitation'
                            title='Wedding Invitation'
                            description='Custom-made interactive gamified wedding invitations.'
                            codeUrl='https://github.com/matusfaro/wedding-invitations'
                            siteUrl='https://matusnomin.com'
                        />
                    </ColumnRow>
                    <ColumnRow>
                        <ColumnItem
                            column={1}
                            logo='/img/kava-logo.svg'
                            logoAlt='Kava'
                            title='Kava'
                            description='Multiplayer 3D avatar app with real-time webcam tracking to animate player body movements and facial expressions.'
                            codeUrl='https://github.com/matusfaro/kava'
                            siteUrl='https://matusfaro.github.io/kava'
                        />
                        <ColumnItem
                            column={2}
                            logo='/img/voxel4d-logo.svg'
                            logoAlt='4D'
                            title='4D'
                            description='Minecraft-like voxel game in four dimensions — swap out any dimension for another in 3D space.'
                            codeUrl='https://github.com/matusfaro/voxel-4d'
                            siteUrl='https://matusfaro.github.io/voxel-4d'
                        />
                    </ColumnRow>
                </React.Fragment>
            )}

            <Grid item xs={12}><Box marginTop={20}>&nbsp;</Box></Grid>

            <Grid item xs={12} sm={6}>
                <Box className={classes.imgProjectContainer}>
                    <img src='/img/ub-2.jpg' className={classes.imgProject}
                         style={{border: '1px solid black', marginTop: 64}}
                         alt='map'/>
                    <img src='/img/to-2.jpg' className={classes.imgProject}
                         style={{border: '1px solid black', marginBottom: 64}}
                         alt='map'/>
                </Box>
            </Grid>
            <Grid item xs={12} sm={6} alignContent='center'>
                <Box textAlign='left' margin={4} marginRight='auto' maxWidth={150}
                     className={classes.getInTouch}>
                    <MuiLink className={classes.link} href="mailto:hello@smotana.com" color="inherit">
                        <Typography variant="h5" component="h2" display='inline'>Get</Typography>
                        <Typography variant="body1" component="div" display='inline'> in touch</Typography>
                    </MuiLink>
                </Box>
            </Grid>
        </Grid>
    );
};

const LandingHeader = () => {
    const classes = useStyles();
    return (
        <Box className={classes.centerVerticalContainer} height='100vh'>
            <Box className={classes.centerVerticalInner}>
                <img src='/img/logo.svg' className={classes.imgFit} alt="logo"/>
                <div className={classes.smotanaBrandName} style={{fontSize: 32}}>smotana</div>
            </Box>
            <ExpandDownIcon fontSize='inherit' className={classes.expandDownIcon}/>
        </Box>
    );
};

const Header = () => {
    const classes = useStyles();
    return (
        <Box>
            <Box marginBottom={5} display='flex' justifyContent='left'>
                <img src='/img/logo.svg' className={classes.imgFit} alt="logo" height='150px'/>
            </Box>
        </Box>
    );
};

const ScrollToTop = () => {
    const {pathname} = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return null;
}
